import React from "react";
import { HoverRows } from "./GeotabHover.jsx";
import { valueOf } from "./geotabDashboardSeries.js";
import { fmtDelta, fmtDuration } from "./geotabFormat.js";

// Graphiques du Tableau de bord Geotab, dessinés comme ceux du Tableau de bord principal (DashboardCharts.css) : la portée choisie
// en plein, les autres journées ou semaines en contexte (data-context, plus pâles), la moyenne en pointillé.
const ACCENT = "var(--color-accent)";
const PLAN = "#94a3b8";
const NUMBER = new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 1 });

export const dayLabel = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CA", { day: "numeric", month: "short" });
export const longDay = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CA", { weekday: "short", day: "numeric", month: "short" });
const stageOf = (d, key) => (d.durations?.[key]?.count ? d.durations[key] : null);
export const STAGE_LEGEND = [["line", ACCENT, "Réel"], ["line", PLAN, "Prévu"]];

function useSize(initial) {
  const ref = React.useRef(null);
  const [size, setSize] = React.useState(initial);
  React.useEffect(() => {
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0) setSize({ width, height });
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, size];
}

// Trois ou quatre graduations rondes couvrant [lo, hi].
function niceScale(lo, hi) {
  const raw = Math.max(hi - lo, 1e-6) / 3;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= raw);
  const min = Math.floor(lo / step) * step;
  return { min, max: Math.max(min + step, Math.ceil(hi / step) * step), step };
}

// Tracé d'une courbe ; une journée sans mesure coupe la ligne.
function pathOf(points) {
  let pen = false;
  return points.reduce((path, pt) => {
    if (!pt) { pen = false; return path; }
    const part = `${pen ? "L" : "M"}${pt[0].toFixed(1)},${pt[1].toFixed(1)}`;
    pen = true;
    return path + part;
  }, "");
}

export function Legend({ items }) {
  return (
    <div className="geotab-dash-legend">
      {items.map(([shape, color, text]) => <span key={text}><i data-shape={shape} style={color ? { background: color } : undefined} />{text}</span>)}
    </div>
  );
}

// Prévu et réel d'une étape (route, chargement ou livraison), un point par journée : plus la ligne verte suit la grise, plus le
// plan du Dispatch est juste. Le graphique prend la hauteur de son conteneur ; un clic ouvre la journée dans Dispatch vs Geotab.
export function StageDailyChart({ days, stage, label, selected, onOpenDay, showDates = true }) {
  const [ref, measured] = useSize({ width: 760, height: 130 });
  // Sur téléphone, assez de place par journée pour viser du doigt ; le graphique défile alors de côté, comme au Tableau de bord.
  const minWidth = measured.width < 600 ? Math.max(440, days.length * 14 + 60) : 0;
  const width = Math.max(measured.width, minWidth, 320);
  const below = showDates ? 22 : 6;
  const top = 8, plotHeight = Math.max(60, measured.height - top - below), bottom = top + plotHeight, height = bottom + below;
  const values = days.flatMap((d) => { const s = stageOf(d, stage); return s ? [s.planned, s.real] : []; });
  const scale = niceScale(values.length ? Math.max(0, Math.min(...values) - 5) : 0, values.length ? Math.max(...values) + 5 : 60);
  const left = 52, right = width - 6;
  const slot = (right - left) / Math.max(days.length, 1);
  const y = (v) => bottom - ((v - scale.min) / (scale.max - scale.min)) * plotHeight;
  const cx = (i) => left + slot * (i + 0.5);
  const ticks = Array.from({ length: Math.round((scale.max - scale.min) / scale.step) + 1 }, (_, i) => scale.min + i * scale.step);
  const labelEvery = Math.max(1, Math.ceil(days.length / Math.max(4, Math.floor((right - left) / 72))));
  const line = (field) => pathOf(days.map((d, i) => { const s = stageOf(d, stage); return s ? [cx(i), y(s[field])] : null; }));
  return (
    <div ref={ref} className="dashboard-chart-scroll">
      <div className="dashboard-chart-canvas" style={{ "--geotab-chart-min": `${minWidth}px` }} role="region" aria-label={`${label} : prévu et réel par journée`}>
        <svg viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
          {ticks.map((t) => (
            <g key={t}><line x1={left} x2={right} y1={y(t)} y2={y(t)} className="dashboard-chart-grid" />
              <text x={left - 7} y={y(t) + 4} textAnchor="end" className="dashboard-chart-label">{`${NUMBER.format(t)} min`}</text></g>
          ))}
          <path d={line("planned")} fill="none" stroke={PLAN} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          <path d={line("real")} fill="none" stroke={ACCENT} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
          {days.map((d, i) => {
            const s = stageOf(d, stage);
            return (
              <g key={d.date} data-context={selected.has(d.date) ? undefined : "true"}>
                {s && <circle cx={cx(i)} cy={y(s.real)} r="3" className="dashboard-chart-point" />}
                {showDates && i % labelEvery === 0 && <text x={cx(i)} y={bottom + 16} textAnchor="middle" className="dashboard-chart-label">{dayLabel(d.date)}</text>}
              </g>
            );
          })}
        </svg>
        {days.map((d, i) => {
          const s = stageOf(d, stage);
          const rows = s ? [["Prévu", fmtDuration(s.planned)], ["Réel", fmtDuration(s.real)], ["Écart", fmtDelta(Math.round(s.real) - Math.round(s.planned))],
            ["Mesurés", `${s.count} sur ${s.of}`]] : [["Mesurés", "aucun"]];
          return (
            <HoverRows key={d.date} title={`${label} · ${longDay(d.date)}`} rows={[...rows, ["Clic", "ouvrir Dispatch vs Geotab"]]}>
              <button type="button" className="dashboard-chart-target" onClick={() => onOpenDay(d.date)}
                aria-label={`${longDay(d.date)} : ouvrir la journée dans Dispatch vs Geotab`}
                style={{ left: `${((left + slot * i) / width) * 100}%`, width: `${(slot / width) * 100}%`, top: `${(top / height) * 100}%`, height: `${(plotHeight / height) * 100}%` }} />
            </HoverRows>
          );
        })}
      </div>
    </div>
  );
}

// Une barre par semaine (dimanche-samedi), sa valeur au-dessus ; en vue Semaine, les autres semaines en contexte.
export function WeekChart({ weeks, metric, highlight, average }) {
  const [ref, measured] = useSize({ width: 360, height: 0 });
  const width = Math.max(measured.width, 240);
  const values = weeks.map((w) => valueOf(metric, w));
  const known = values.filter((v) => v != null);
  const lo = Math.min(0, ...known);
  const hi = Math.max(0, ...known, average ?? 0) || 1;
  const top = 18, plotHeight = 108, bottom = top + plotHeight, height = bottom + 22;
  const y = (v) => bottom - ((v - lo) / (hi - lo || 1)) * plotHeight;
  const slot = width / Math.max(weeks.length, 1);
  const barWidth = Math.min(30, slot * 0.55);
  return (
    <div ref={ref} className="dashboard-chart-canvas" style={{ "--geotab-chart-min": "0px" }} role="img"
      aria-label={`${metric.label} par semaine : ${weeks.map((w, i) => `${dayLabel(w.start)} ${values[i] == null ? "—" : metric.format(values[i])}`).join(", ")}`}>
      <svg viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
        <line x1="0" x2={width} y1={y(0)} y2={y(0)} className="dashboard-chart-grid" />
        {weeks.map((w, i) => {
          const v = values[i];
          const x = slot * (i + 0.5);
          return (
            <g key={w.start} data-context={highlight && w.start !== highlight ? "true" : undefined}>
              {v != null && <rect x={x - barWidth / 2} y={y(Math.max(0, v))} width={barWidth} height={Math.max(1, Math.abs(y(v) - y(0)))} rx="2" fill={ACCENT} />}
              {v != null && <text x={x} y={v >= 0 ? y(v) - 5 : y(v) + 13} textAnchor="middle" className="dashboard-chart-label font-bold">{metric.format(v)}</text>}
              <text x={x} y={height - 5} textAnchor="middle" className="dashboard-chart-label">{dayLabel(w.start)}</text>
            </g>
          );
        })}
        {average != null && <line x1="0" x2={width} y1={y(average)} y2={y(average)} className="dashboard-chart-average" />}
      </svg>
    </div>
  );
}
